import { action, makeObservable, observable } from 'mobx';

import { IApiClient } from '../../../../../data/client';
import type { ModularConfigurationData, ShoppingContext } from '../../../../../data/model';
import { GenerateModularConfigurationCodeCommand } from '../../../../../data/model';
import { AsyncCommand } from '../../../../../shared/common';
import Logger from '../../../../../shared/Logger';
import { INavigationService } from '../../../../../shared/NavigationService';
import { IShareModularConfiguratorUrlProvider } from '../../../../../shared/ShareModularConfiguratorUrlProvider';
import { StoreUrl } from '../../../../../shared/StoreUrl';
import { ICancellableTask, RunLastTasksScheduler } from '../../../../../shared/TasksScheduler';

class ModularConfiguratorSummaryState {
  @observable
  public configurationCode = '';

  public isPurchaseEnabled = false;

  private generateConfigurationCodeScheduler = new RunLastTasksScheduler((task: GenerateModularConfigurationCodeTask) =>
    this.executeGenerateConfigurationCodeTask(task),
  );

  public readonly proceedToCheckoutCommand = new AsyncCommand(async () => {
    // TODO: Implement
  });

  public readonly proceedToRequestQuoteCommand = new AsyncCommand(() => this.proceedToRequestQuote());

  public readonly downloadAsPdfCommand = new AsyncCommand(async () => {
    // TODO: Implement
  });

  public readonly downloadZipCommand = new AsyncCommand(async () => {
    // TODO: Implement
  });

  public get isGeneratingConfigurationCode() {
    return this.generateConfigurationCodeScheduler.loading;
  }

  public get isGenerateConfigurationCodeError() {
    return this.generateConfigurationCodeScheduler.error;
  }

  public get shareUrl() {
    return this.shareConfiguratorUrlProvider.getUrl(this.configurationCode);
  }

  public get pdfName() {
    // TODO: Implement
    return `Profim-Revo-${this.configurationCode}.pdf`;
  }

  public get zipName() {
    // TODO: Implement
    return `Profim-Revo-${this.configurationCode}.zip`;
  }

  public constructor(
    private readonly client: IApiClient,
    private readonly navigation: INavigationService,
    private readonly shareConfiguratorUrlProvider: IShareModularConfiguratorUrlProvider,
    private readonly shoppingContext: ShoppingContext,
  ) {
    makeObservable(this);
  }

  public async generateConfigurationCode(configuration: ModularConfigurationData) {
    return this.generateConfigurationCodeScheduler.run(new GenerateModularConfigurationCodeTask(configuration));
  }

  private async executeGenerateConfigurationCodeTask(task: GenerateModularConfigurationCodeTask) {
    try {
      const response = await this.client.send(
        new GenerateModularConfigurationCodeCommand({
          modularConfigurationData: task.configuration,
        }),
      );

      if (task.cancelled || !response) {
        return;
      }

      this.setConfigurationCode(response.modularCode);
    } catch (error) {
      Logger.exception('Error occurred while generating modular configuration code', error);
      throw error;
    }
  }

  private async proceedToRequestQuote() {
    this.navigation.navigateTo(
      StoreUrl.requestQuoteUrl({
        configurationCode: this.configurationCode,
      }),
    );
  }

  @action
  private setConfigurationCode(value: string) {
    this.configurationCode = value;
  }
}

export default ModularConfiguratorSummaryState;

class GenerateModularConfigurationCodeTask implements ICancellableTask {
  configuration: ModularConfigurationData;
  cancelled = false;

  constructor(configuration: ModularConfigurationData) {
    this.configuration = configuration;
  }
}
